---
title: "Installing BookBurrow"
slug: "installing-bookburrow"
category: "getting-started"
description: "Download the installer, run it, and open BookBurrow for the first time — usually under five minutes."
tags: ["install", "windows", "linux", "download", "setup", "it"]
order: 1
---

BookBurrow is a proper desktop application. You download it once, install it like any other piece of software, and it runs locally on your library computer — no browser tabs, no logins every morning, no Wi-Fi required to open it.

This guide walks through installing BookBurrow on a school computer, from downloading the installer to opening the app for the first time.

## What you'll need

- A **Windows 10 or newer** computer, or a Linux laptop running **Ubuntu 22.04+** / **Debian 12+**.
- About **100 MB** of free disk space.
- Permission to install software. In most UK schools a teacher can install standard software; in lock-down environments you may need IT to approve the installer (see the section below).
- A **trial or full licence key**, which you'll find in the portal after signing up at [bookburrow.uk](/signup).

<Callout variant="tip" title="How long does this take?">
If you already have your licence key and admin rights, start to finish is about three minutes. Add a few more minutes if you need to wait for IT to unblock the installer.
</Callout>

## Step 1 — Download the installer

<Steps>
  <Step title="Sign in to the portal">
    Open [bookburrow.uk/portal/login](/portal/login) and sign in with the email address you used when signing up.
  </Step>
  <Step title="Go to Downloads">
    In the left-hand menu, click **Downloads**. You'll see the latest version of BookBurrow for Windows and for Linux.
  </Step>
  <Step title="Pick the right file">
    On Windows, click **Download for Windows** — this gives you a file called `bookburrow-setup-X.Y.Z.msi`. On Linux, download the **AppImage** instead.
  </Step>
</Steps>

The download is small (around 60 MB) and should finish in seconds on a school network.

## Step 2 — Run the installer

### On Windows

<Steps>
  <Step title="Double-click the .msi file">
    Your downloads folder is usually the easiest place to find it. Windows will open a standard installer window.
  </Step>
  <Step title="Click Next, then Install">
    There are no complicated options. BookBurrow installs to your user folder by default so you don't need admin rights, but if you do have admin rights you can tick **Install for all users** to make it available on any Windows account on that computer.
  </Step>
  <Step title="Click Finish">
    The installer creates a Start menu shortcut and, if you tick the option, a desktop shortcut. The whole thing takes about thirty seconds.
  </Step>
</Steps>

### On Linux

<Steps>
  <Step title="Make the AppImage executable">
    Right-click the downloaded file, choose **Properties**, and tick **Allow executing file as program**. (Or run `chmod +x bookburrow-X.Y.Z.AppImage` in a terminal.)
  </Step>
  <Step title="Double-click to run">
    The first time you run it, BookBurrow will offer to integrate itself into your application menu — say yes and you'll have a normal launcher from then on.
  </Step>
</Steps>

## Step 3 — Open BookBurrow and create your library

When you open BookBurrow for the first time, it will ask where to store your library data. The default — in your user folder — is fine for most schools, but we recommend choosing a location that's automatically backed up (OneDrive, SharePoint, or a school network drive) so backups and the live database are both safe.

You'll also be asked for your **school name** (used on reports and borrower cards) and your **first admin PIN**. Pick a four-digit PIN that only staff will know — this protects settings and admin features from curious pupils.

<Callout variant="tip" title="You're now on the trial">
Without activating a licence key, BookBurrow runs in full-featured trial mode for 30 days. That's plenty of time to catalogue some books, import your borrowers, and decide whether to buy. See [Activating your licence](/help/guides/activating-your-licence) when you're ready.
</Callout>

## If your school IT blocks the installer

Some schools run Windows in a locked-down mode where only IT can install software. If BookBurrow's installer is blocked, the quickest route is to forward this information to your IT contact:

- BookBurrow is a **signed .msi** from Roguelite Software Ltd.
- It installs to the **user folder** by default and does not need admin rights.
- It installs **no background services**, **no drivers**, and **no telemetry agents**.
- It only needs outbound HTTPS on port 443 to talk to `api.bookburrow.uk` (updates and ISBN lookups) and `openlibrary.org` / `books.google.com` (book metadata).
- It stores all data in a **single SQLite file** in the user profile, with no external database.

Most IT teams approve it in a matter of minutes once they've read that list.

## Next steps

Now that BookBurrow is installed, your next two jobs are:

1. **[Activate your licence](/help/guides/activating-your-licence)** if you've already bought — or carry on with the trial.
2. **[Run your first loan](/help/guides/quick-start-first-loan)** to prove the whole thing works, end to end, in two minutes.
